import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox, Fieldset, FormElement, Input, Label } from '@feathr/components';
import { flattenError, getIconForAction } from '@feathr/hooks';

import { getInteractionTypeOptions } from '../GroupExportModal.utils';
import TimeFrameAndEmailFields from '../TimeFrameAndEmailFields';
import type { IEmailActivityTabState } from './EmailActivityTab.hooks';

export interface IEmailActivityTabProps {
  state: IEmailActivityTabState;
}

function EmailActivityTab({ state }: Readonly<IEmailActivityTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    exportName,
    exportNameHadFocus,
    exportNameError,
    selectedInteractionTypes,
    interactionTypesError,
    handleChangeExportName,
    handleExportNameBlur,
    handleToggleInteractionType,
  } = state;

  const exportNameValidationError = exportNameHadFocus ? flattenError(exportNameError) : undefined;

  const interactionTypeOptions = getInteractionTypeOptions(t);

  const interactionTypeTooltip = (
    <Trans t={t}>
      <p>
        <strong>Delivery:</strong> the email reached the recipient
      </p>
      <p>
        <strong>Open:</strong> the recipient opened the email
      </p>
      <p>
        <strong>Click:</strong> the recipient clicked a link in the email
      </p>
    </Trans>
  );

  const interactionTypeLabel = (
    <Label
      margin={'condensed'}
      tooltip={interactionTypeTooltip}
      tooltipIcon={getIconForAction('question')}
    >
      {t('Email interaction type')}
    </Label>
  );

  return (
    <Fieldset>
      <Stack gap={theme.spacing['4']}>
        <Input
          isClearable={true}
          label={t('Export name')}
          onBlur={handleExportNameBlur}
          onChange={handleChangeExportName}
          type={'text'}
          validationError={exportNameValidationError}
          value={exportName}
        />
        <FormElement
          label={interactionTypeLabel}
          validationError={flattenError(interactionTypesError)}
        >
          <Stack gap={theme.spacing['2']}>
            {interactionTypeOptions.map((option) => (
              <Checkbox
                key={option.id}
                label={option.label}
                onChange={(): void => handleToggleInteractionType(option.id)}
                value={selectedInteractionTypes.includes(option.id)}
              />
            ))}
          </Stack>
        </FormElement>
        <TimeFrameAndEmailFields state={state} />
      </Stack>
    </Fieldset>
  );
}

export default EmailActivityTab;
